import {
    createContext,
    useCallback,
    useContext,
    useLayoutEffect,
    useMemo,
    type ReactNode
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import fa from "../i18n/fa/fa";
import en from "../i18n/en/en";

type LanguageCode = "fa" | "en";
type Direction = "rtl" | "ltr";

// The Persian file is the source of truth for translation keys.
// The English file must have the same shape.
type Translations = typeof fa;

interface LanguageConfig {
    code: LanguageCode;
    dir: Direction;
    translations: Translations;
}

interface LanguageContextValue {
    lang: LanguageCode;
    dir: Direction;
    isRTL: boolean;
    t: Translations;
    changeLanguage: (newLang: LanguageCode) => void;
}

const LANGUAGES: Record<LanguageCode, LanguageConfig> = {
    fa: { code: "fa", dir: "rtl", translations: fa },
    en: { code: "en", dir: "ltr", translations: en }
};

const DEFAULT_LANGUAGE: LanguageCode = "fa";

// Matches the language prefix of a path: /fa, /fa/blog, /en/courses/1...
const LANGUAGE_PREFIX = /^\/(fa|en)(?:\/|$)/;

const LanguageContext = createContext<LanguageContextValue | null>(null);

interface LanguageProviderProps {
    children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
    const location = useLocation();
    const navigate = useNavigate();

    // The URL is the single source of truth for the active language
    const lang =
        (location.pathname.match(LANGUAGE_PREFIX)?.[1] as LanguageCode | undefined) ??
        DEFAULT_LANGUAGE;

    const current = LANGUAGES[lang];

    // Keep <html lang> and <html dir> in sync before the browser paints
    useLayoutEffect(() => {
        document.documentElement.lang = current.code;
        document.documentElement.dir = current.dir;
    }, [current.code, current.dir]);

    // Swaps the language prefix and keeps the rest of the path: /fa/blog -> /en/blog
    const changeLanguage = useCallback(
        (newLang: LanguageCode) => {
            if (!LANGUAGES[newLang]) return;

            const rest = location.pathname.replace(/^\/(fa|en)/, "");
            navigate(`/${newLang}${rest}`);
        },
        [location.pathname, navigate]
    );

    const value = useMemo<LanguageContextValue>(
        () => ({
            lang: current.code,
            dir: current.dir,
            isRTL: current.dir === "rtl",
            t: current.translations,
            changeLanguage
        }),
        [current, changeLanguage]
    );

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useLanguage(): LanguageContextValue {
    const context = useContext(LanguageContext);

    // Fail loudly instead of returning null and crashing later on context.t
    if (!context) {
        throw new Error("useLanguage must be used inside LanguageProvider");
    }

    return context;
}